import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useCompanyInfo, useReviews } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function KompositdorPage() {
  const companyInfo = useCompanyInfo();
  const reviews = useReviews();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { 
      src: "/_assets/img20251229122934.jpg", 
      title: "Havedør i galvaniseret stålramme med kompositprofiler",
      desc: "Robust udendørs dør monteret mellem hvidpudsede mure."
    },
    { 
      src: "/_assets/pxl-20260611-130115349.jpg", 
      title: "Skurdør / udhusdør i komposit og stål",
      desc: "Specialfremstillet dør i stålramme monteret plan i facade."
    },
    { 
      src: "/_assets/pxl-20260608-094742985-mp.jpg", 
      title: "Kompositdør med sikkerhedspigge",
      desc: "Ekstra sikring foroven monteret i havemur."
    },
    { 
      src: "/_assets/pxl-20260615-135924900.jpg", 
      title: "Kompositdør mellem murede murstenssøjler",
      desc: "Mørkebrune kompositlameller og galvaniseret ramme."
    },
    { 
      src: "/_assets/img20251229122541.jpg", 
      title: "Inspektionslem og kompositdør under udhæng",
      desc: "Specialmål med integreret stålramme og kompositbeklædning."
    },
    { 
      src: "/_assets/img20260322162839.jpg", 
      title: "Kompositdør og dobbeltfløj i stålkonstruktion",
      desc: "Galvaniseret ramme med elegante sorte profiler."
    },
  ];

  const fordele = [
    {
      icon: "fa-shield-halved",
      title: "100% vedligeholdelsesfri",
      desc: "Slipper for slibning, maling og råd. Kompositbrædderne skal blot vaskes med vand og sæbe ved behov."
    },
    {
      icon: "fa-ruler-combined",
      title: "Specialfremstillet på mål",
      desc: "Vi fremstiller kompositdøre på præcist de mål og den åbning, du har – uanset om det er i mur, hegn eller træfacade."
    },
    {
      icon: "fa-lock",
      title: "Robust stålramme & sikkerhed",
      desc: "Bygget på en kraftig, varmgalvaniseret eller pulverlakeret stålramme med mulighed for cylinderlås, dørgreb og pigge."
    },
    {
      icon: "fa-palette",
      title: "Flot træstruktur & moderne farver",
      desc: "Vælg mellem koksgrå, sort eller naturbrun finish, som passer harmonisk til dit hegn, have eller murværk."
    }
  ];

  return (
    <>
      <SEO
        title="Kompositdør | Skræddersyede døre i komposit og stål | Hovendals"
        description="Få en flot, vedligeholdelsesfri kompositdør lavet på specialmål. Ideel som havedør, skurdør eller passage mellem havemure. Kontakt Hovendals for uforpligtende tilbud."
        ogImage="/_assets/img20251229122934.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/img20251229122934.jpg", "width=1600,quality=80,format=auto")}
            alt="Kompositdør i stålramme"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-door-closed" aria-hidden="true"></i>
              <span>Ny løsning</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Kompositdør – stærk stålramme og vedligeholdelsesfrit design
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              Skræddersyede udendørs kompositdøre til havemure, skure, carporte og passager. 
              Kombinationen af kraftigt galvaniseret stål og moderne komposit giver en uovertruffen holdbarhed.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
              >
                <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                <span>Ring: {companyInfo.phone}</span>
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm transition-all flex items-center gap-2 border border-slate-700"
              >
                <i className="fa-sharp fa-solid fa-paper-plane" aria-hidden="true"></i>
                <span>Få uforpligtende tilbud</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section 1 */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Skræddersyet håndværk</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  En holdbar kompositdør til dit uderum
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Mangler du en solid dør til haven, redskabsrummet, skuret eller mellem to murede stolper? 
                En <strong>kompositdør</strong> fra Hovendals forener det bedste fra to verdener: 
                en formstabil og vejrbestandig stålramme kombineret med æstetiske kompositlameller med flot træstruktur.
              </p>
              <p className="text-slate-700 leading-relaxed text-base">
                I modsætning til traditionelle trædøre, som med årene kan slå sig, blegne eller rådne i det omskiftelige danske klima, 
                holder en kompositdør sin form og farve år efter år uden behov for træbeskyttelse eller maling.
              </p>

              <div className="space-y-3 pt-2">
                <h3 className="text-lg font-bold text-slate-900">Typiske anvendelser:</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#004fa4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Havedør i murværk:</strong> Elegant gennemgangsdør monteret direkte i havemur eller gavl.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#004fa4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Skur- og udhusdør:</strong> Planforsænket eller udenpåliggende dør til skur, værksted eller cykelskur.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#004fa4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Inspektions- og depotlem:</strong> Mindre specialdøre til krybekælder, loft eller teknikskjuler.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-sharp fa-solid fa-circle-check text-[#004fa4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Integreret i hegn:</strong> Matcher 1:1 med vores populære <Link to="/komposithegn" className="text-[#004fa4] font-bold hover:underline">komposithegn</Link>.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/img20251229122934.jpg", "width=800,quality=85,format=auto")}
                alt="Monteret kompositdør"
                className="w-full h-auto object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/80 to-transparent p-6 text-white">
                <p className="text-sm font-semibold">Specialmonteret havedør med stålramme i hvidkalket havemur</p>
              </div>
            </div>
          </div>

          {/* Fordele gitter */}
          <div className="mb-16">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Hvorfor vælge komposit</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                Fordele ved en kompositdør fra Hovendals
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {fordele.map((f, i) => (
                <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-200/80 hover:border-[#004fa4] hover:shadow-md transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#004fa4]/10 text-[#004fa4] flex items-center justify-center text-xl mb-4">
                    <i className={`fa-sharp fa-solid ${f.icon}`} aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Galleri over kompositdøre */}
          <div className="mb-16">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Referencer</span>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Eksempler på kompositdøre vi har leveret
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Klik på et foto for at se detaljerne i fuld størrelse.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#004fa4] hover:text-[#183e7f]"
              >
                <span>Få en pris på dine mål</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative rounded-2xl overflow-hidden bg-white border border-slate-200 cursor-pointer shadow-sm hover:shadow-xl transition-all flex flex-col"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100 relative">
                    <img
                      src={imageUrl(img.src, "width=600,quality=85,format=auto")}
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <span className="p-3 rounded-full bg-[#143463]/90 text-white text-sm shadow-md">
                        <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-bold text-slate-900 text-base group-hover:text-[#004fa4] transition-colors mb-1">
                        {img.title}
                      </h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        {img.desc}
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-[#004fa4] font-semibold">
                      <span>Se fuld visning</span>
                      <i className="fa-sharp fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Specialmål boks */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 sm:p-12 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2 space-y-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#c2ccdf]">
                  Landsdækkende montering
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                  Opmåling, produktion og professionel montage
                </h3>
                <p className="text-slate-300 leading-relaxed text-base">
                  Hos Hovendals varetager vi hele forløbet fra start til slut. Vi tager en grundig dialog om dine ønsker til farve, beslag og mål. Døren svejses og samles på vores eget værksted på Fyn og monteres sikkert på din adresse af erfarne klejnsmede.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <a
                    href={`tel:${companyInfo.phoneFormatted}`}
                    className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                    <span>Ring til Simon: {companyInfo.phone}</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm transition-all flex items-center gap-2 border border-slate-700"
                  >
                    <span>Send forespørgsel</span>
                  </Link>
                </div>
              </div>

              <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-[#004FA4]/20 text-[#C2CCDF] flex items-center justify-center mx-auto text-xl">
                  <i className="fa-sharp fa-solid fa-check-double" aria-hidden="true"></i>
                </div>
                <h4 className="font-bold text-white text-lg">Hurtig opfølgning</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Send os gerne et billede og omtrentlige mål af din åbning, så kvitterer vi hurtigt med et overslag eller tilbud.
                </p>
                <Link
                  to="/kontakt"
                  className="inline-block text-xs font-bold text-[#c2ccdf] hover:text-[#c2ccdf] underline"
                >
                  Gå til kontaktformular
                </Link>
              </div>
            </div>
          </div>

          {/* Anmeldelser */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Hvad siger kunderne om Hovendals?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((rev, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1 text-[#004fa4] text-xs mb-2">
                      {[...Array(rev.rating)].map((_, i) => (
                        <i key={i} className="fa-sharp fa-solid fa-star" aria-hidden="true"></i>
                      ))}
                    </div>
                    <div className="font-bold text-slate-900 text-sm mb-2">{rev.title}</div>
                    <p className="text-xs text-slate-600 italic leading-relaxed mb-4">"{rev.text}"</p>
                  </div>
                  <div className="text-xs font-bold text-slate-800">{rev.author}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        images={images.map(img => ({ src: img.src, title: img.title }))}
        currentIndex={lightboxIndex || 0}
        onNavigate={(newIdx) => setLightboxIndex(newIdx)}
      />
    </>
  );
}
